import React, { useEffect, useMemo } from 'react';

import isEmpty from 'lodash/isEmpty';
import remove from 'lodash/remove';
import { useDropzone } from 'react-dropzone';

import {
  AttachmentFile,
  AttachmentsForDialog,
} from '@qality/common-model/src/Attachment';
import {
  AttachmentHelper,
  StorageOfUniqueDialogs,
  StorageOfUniqueServices,
} from '@qality/common-service';
import { Utils } from '@qality/common-utils';

import AttachmentsNewThumb from './AttachmentsNewThumb';
import EnableAttachments from './EnableAttachments';
import TestCaseBoxAttachmentButton from './TestCaseBoxAttachmentButton';
import './_test-case-box-step.scss';

type Props = {
  onAttachmentFilesChanged: (files: AttachmentsForDialog) => Promise<void>;
  files: Array<AttachmentFile>;
  canCreateAttachment: boolean;
  canEdit: boolean;
  pastedAttachments: Array<File>;
  clearPastedAttachments: () => void;
};
/*eslint max-lines-per-function: ["error", 200]*/
/*eslint complexity: ["error", 8]*/
export default function TestCaseBoxNewAttachments({
  onAttachmentFilesChanged,
  files,
  canCreateAttachment,
  canEdit,
  pastedAttachments,
  clearPastedAttachments,
}: Props): JSX.Element {
  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
    open,
  } = useDropzone({
    noClick: true,
    noKeyboard: true,
    disabled: !canCreateAttachment && !canEdit,
    noDrag: !canCreateAttachment || !canEdit,
    onDrop: (acceptedFiles) => {
      const attachment = AttachmentHelper.attachmentURLCreator(acceptedFiles);
      onAttachmentFilesChanged([...attachment, ...files]);
    },
  });

  useEffect(() => {
    if (!Utils.isNilOrEmpty(pastedAttachments)) {
      const attachment =
        AttachmentHelper.attachmentURLCreator(pastedAttachments);
      onAttachmentFilesChanged([...attachment, ...files]);
      clearPastedAttachments();
    }
  }, [pastedAttachments]);

  const style = useMemo(
    () => ({
      ...(isDragActive ? activeStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isDragActive, isDragReject, isDragAccept]
  );

  const removeAttachment = (index) => {
    onAttachmentFilesChanged(remove(files, (item, i) => i !== index));
  };

  const enlargeAttachment = (index, isOpen) => {
    StorageOfUniqueDialogs.get('AddAttachmentDialog').openAttachmentDialog(
      index,
      files,
      isOpen
    );
  };

  return (
    <div>
      {!StorageOfUniqueServices.get(
        'AttachmentConfigService'
      ).areAttachmentsEnabled() ? (
        <EnableAttachments />
      ) : (
        <div className='new-drop-container'>
          <div
            {...getRootProps({
              style,
              className: isEmpty(files)
                ? 'new-drop-container-empty'
                : 'new-drop-container-filled',
            })}
          >
            <input {...getInputProps()} />
            {files.map((file, index) => {
              return (
                <AttachmentsNewThumb
                  key={index}
                  file={file}
                  index={index}
                  removeAttachment={removeAttachment}
                  enlargeAttachment={enlargeAttachment}
                />
              );
            })}
            <TestCaseBoxAttachmentButton
              onClick={open}
              isInNewStep
              isHovered
              isVisible={canCreateAttachment && canEdit}
            />
          </div>
        </div>
      )}
    </div>
  );
}

const activeStyle = {
  borderColor: 'var(--ds-border-accent-blue, var(--ds-fb-border-accent-blue))',
};

const acceptStyle = {
  borderColor:
    'var(--ds-border-accent-green, var(--ds-fb-border-accent-green))',
};

const rejectStyle = {
  borderColor: 'var(--ds-border-accent-red, var(--ds-fb-border-accent-red))',
};
